/**
 * FOC calculator — client-side only, no data sent to Shopify.
 * Canonical values: arrow length and balance in inches (0.01 precision).
 * Number fields use commit-on-blur + in-range live updates to allow free typing.
 */
(function () {
  var CM_PER_IN = 2.54;
  var LEN_MIN = 24;
  var LEN_MAX = 33;
  var BP_MIN = 5;
  var BP_MAX = 30;
  /** Smallest step for sliders and rounding (two decimal places). */
  var STEP = 0.01;

  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function getBpMaxIn(lenIn) {
    return Math.min(BP_MAX, round2(lenIn - STEP));
  }

  function formatMeasFromInches(inches, u) {
    var v = u === 'cm' ? round2(inches * CM_PER_IN) : round2(inches);
    var s = v.toFixed(2);
    if (s.indexOf('.') >= 0) {
      s = s.replace(/0+$/, '');
      s = s.replace(/\.$/, '');
    }
    return s;
  }

  function rawDisplayToInches(raw, u) {
    if (raw == null) return null;
    var t = String(raw).trim();
    if (t === '' || t === '-' || t === '.' || t === '-.') return null;
    var p = parseFloat(t);
    if (isNaN(p)) return null;
    return u === 'cm' ? round2(p / CM_PER_IN) : round2(p);
  }

  /** Live update from field only if value is fully in valid range (avoids clamping while typing "23"). */
  function tryCommitLenInches(raw, u) {
    var inches = rawDisplayToInches(raw, u);
    if (inches == null) return null;
    if (inches < LEN_MIN || inches > LEN_MAX) return null;
    return inches;
  }

  function tryCommitBpInches(raw, lenIn, u) {
    var inches = rawDisplayToInches(raw, u);
    if (inches == null) return null;
    var bpMax = getBpMaxIn(lenIn);
    if (inches < BP_MIN || inches > bpMax) return null;
    if (inches >= lenIn - 1e-9) return null;
    return inches;
  }

  function blurCommitLen(raw, prevLenIn, u) {
    var inches = rawDisplayToInches(raw, u);
    if (raw == null || String(raw).trim() === '' || inches == null || isNaN(inches)) {
      return prevLenIn;
    }
    return clamp(round2(inches), LEN_MIN, LEN_MAX);
  }

  function blurCommitBp(raw, lenIn, prevBpIn, u) {
    var inches = rawDisplayToInches(raw, u);
    if (raw == null || String(raw).trim() === '' || inches == null || isNaN(inches)) {
      return prevBpIn;
    }
    var bpMax = getBpMaxIn(lenIn);
    var v = clamp(round2(inches), BP_MIN, bpMax);
    if (v >= lenIn - STEP / 2) v = getBpMaxIn(lenIn);
    return v;
  }

  function getRating(foc) {
    if (foc < 7) {
      return {
        label: 'TOO LOW',
        color: 'var(--colorCartDot, #ff4f33)',
        desc: 'Arrow stability is poor. Your arrow may fishtail or plane during flight. Try a heavier point or remove weight from the rear of the arrow.',
      };
    }
    if (foc <= 11) {
      return {
        label: 'GOOD',
        color: '#5cb85c',
        desc: 'Solid range for most target archery and 3D shooting. You have a good balance of speed and downrange stability.',
      };
    }
    if (foc <= 15) {
      return {
        label: 'EXCELLENT',
        /* Deep royal blue: distinct from TOO LOW (red) and GOOD (green); white text on rating tile passes contrast */
        color: '#1e3a8a',
        desc: 'Prime range for hunting and field archery. Strong front-end stability delivers consistent flight and reliable penetration on game.',
      };
    }
    if (foc <= 25) {
      return {
        label: 'HIGH FOC',
        color: '#f0a500',
        desc: 'Very front-heavy setup. Excellent penetration for big game — especially tight. Expect a steeper trajectory at longer distances.',
      };
    }
    return {
      label: 'EXTREME FOC',
      color: '#f0a500',
      desc: 'Extreme front of center. Specialized big-game hunting setup. Outstanding penetration but trajectory will suffer at range.',
    };
  }

  function isFocDiagramMobile() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 600px)').matches
    );
  }

  /** Desktop: extra horizontal slack for wide pills. Mobile: tighter crop so the arrow scales larger (padding alone barely helps — viewBox drives letterboxing). */
  var DIAGRAM_VIEWBOX_DESKTOP = '-76 -18 628 124';
  /** When balance ≥ length or state missing: safe wide crop so pills are not clipped. */
  var DIAGRAM_VIEWBOX_MOBILE_FALLBACK = '0 -18 532 124';
  /** Track and tip geometry (must match foc-calculator.liquid SVG). */
  var DIAGRAM_TRACK_S = 22;
  var DIAGRAM_TRACK_E = 440;
  /** Rightmost x of tip polygon + small margin for TIP label (x=448, end). */
  var DIAGRAM_TIP_RIGHT_MIN = 460;
  /** Mobile balance pill is 152 wide; group is translate(bpX), rect x=-76 → extends bpX ± 76. */
  var DIAGRAM_MOBILE_BALANCE_HALF = 76;
  var DIAGRAM_MOBILE_VB_PAD_PILL = 6;

  /**
   * Mobile: crop the right edge to max(tip+label, balance pill) so empty space past the tip
   * matches the tighter left crop when the pill is left; widen when the pill moves right.
   */
  function applyMobileDiagramViewBox(svg, bpIn, lenIn) {
    if (lenIn <= 0 || bpIn >= lenIn - 1e-9) {
      svg.setAttribute('viewBox', DIAGRAM_VIEWBOX_MOBILE_FALLBACK);
      return;
    }
    var range = DIAGRAM_TRACK_E - DIAGRAM_TRACK_S;
    var bpX = DIAGRAM_TRACK_S + (bpIn / lenIn) * range;
    var rightBound = Math.max(
      DIAGRAM_TIP_RIGHT_MIN,
      bpX + DIAGRAM_MOBILE_BALANCE_HALF + DIAGRAM_MOBILE_VB_PAD_PILL
    );
    var minX = 0;
    var w = rightBound - minX;
    svg.setAttribute('viewBox', minX + ' -18 ' + w + ' 124');
  }

  function syncDiagramViewBox(root) {
    var svg = root.querySelector('.calc-foc__diagram svg');
    if (!svg) return;
    if (!isFocDiagramMobile()) {
      svg.setAttribute('viewBox', DIAGRAM_VIEWBOX_DESKTOP);
      return;
    }
    var lenStr = root.getAttribute('data-foc-diagram-len');
    var bpStr = root.getAttribute('data-foc-diagram-bp');
    var lenIn = lenStr != null && lenStr !== '' ? parseFloat(lenStr) : NaN;
    var bpIn = bpStr != null && bpStr !== '' ? parseFloat(bpStr) : NaN;
    if (isNaN(lenIn) || lenIn <= 0) lenIn = 28.5;
    if (isNaN(bpIn)) bpIn = 16;
    applyMobileDiagramViewBox(svg, bpIn, lenIn);
  }

  /**
   * Mobile pill rects + foreignObject must use setAttribute — theme CSS often does not override
   * SVG presentation attributes in WebKit, so HTML keeps width="60" and labels clip / misalign.
   */
  function syncDiagramPillGeometry(root) {
    var bpLabel = root.querySelector('[data-diagram-bp-label]');
    var midLabel = root.querySelector('[data-diagram-mid-label]');
    if (!bpLabel || !midLabel) return;
    var bgB = bpLabel.querySelector('.calc-foc__diagram-pill-bg-balance');
    var foB = bpLabel.querySelector('.calc-foc__diagram-pill-fo-balance');
    var bgM = midLabel.querySelector('.calc-foc__diagram-pill-bg-mid');
    var foM = midLabel.querySelector('.calc-foc__diagram-pill-fo-mid');
    var mobile = isFocDiagramMobile();
    if (mobile) {
      if (bgB) {
        bgB.setAttribute('x', '-76');
        bgB.setAttribute('y', '-34');
        bgB.setAttribute('width', '152');
        bgB.setAttribute('height', '34');
        bgB.setAttribute('rx', '17');
      }
      if (foB) {
        foB.setAttribute('x', '-76');
        foB.setAttribute('y', '-34');
        foB.setAttribute('width', '152');
        foB.setAttribute('height', '34');
      }
      if (bgM) {
        bgM.setAttribute('x', '-48');
        bgM.setAttribute('y', '6');
        bgM.setAttribute('width', '96');
        bgM.setAttribute('height', '34');
        bgM.setAttribute('rx', '17');
      }
      if (foM) {
        foM.setAttribute('x', '-48');
        foM.setAttribute('y', '6');
        foM.setAttribute('width', '96');
        foM.setAttribute('height', '34');
      }
    } else {
      if (bgB) {
        bgB.setAttribute('x', '-30');
        bgB.setAttribute('y', '-24');
        bgB.setAttribute('width', '60');
        bgB.setAttribute('height', '24');
        bgB.setAttribute('rx', '12');
      }
      if (foB) {
        foB.setAttribute('x', '-30');
        foB.setAttribute('y', '-24');
        foB.setAttribute('width', '60');
        foB.setAttribute('height', '24');
      }
      if (bgM) {
        bgM.setAttribute('x', '-30');
        bgM.setAttribute('y', '6');
        bgM.setAttribute('width', '60');
        bgM.setAttribute('height', '24');
        bgM.setAttribute('rx', '12');
      }
      if (foM) {
        foM.setAttribute('x', '-30');
        foM.setAttribute('y', '6');
        foM.setAttribute('width', '60');
        foM.setAttribute('height', '24');
      }
    }
  }

  /** Desktop: SVG <text> attrs. Mobile uses foreignObject+HTML (sync skipped) — WebKit still breaks <text> under transformed <g>. */
  function syncDiagramPillTextLayout(root) {
    var bpTxt = root.querySelector('[data-diagram-bp-txt]');
    var midTxt = root.querySelector('[data-diagram-mid-txt]');
    if (!bpTxt || !midTxt) return;
    if (isFocDiagramMobile()) {
      return;
    }
    bpTxt.setAttribute('x', '0');
    midTxt.setAttribute('x', '0');
    bpTxt.setAttribute('text-anchor', 'middle');
    midTxt.setAttribute('text-anchor', 'middle');
    bpTxt.setAttribute('y', '-12');
    midTxt.setAttribute('y', '18');
    bpTxt.setAttribute('dominant-baseline', 'central');
    midTxt.setAttribute('dominant-baseline', 'central');
  }

  var pillLayoutListenersBound = false;
  function ensureDiagramPillLayoutListeners() {
    if (pillLayoutListenersBound) return;
    pillLayoutListenersBound = true;
    var debounce;
    function syncAll() {
      document.querySelectorAll('[data-foc-calculator]').forEach(function (r) {
        syncDiagramViewBox(r);
        syncDiagramPillGeometry(r);
        syncDiagramPillTextLayout(r);
      });
    }
    window.addEventListener('resize', function () {
      clearTimeout(debounce);
      debounce = setTimeout(syncAll, 100);
    });
    if (typeof window.matchMedia === 'function') {
      var mql = window.matchMedia('(max-width: 600px)');
      if (mql.addEventListener) {
        mql.addEventListener('change', syncAll);
      } else if (mql.addListener) {
        mql.addListener(syncAll);
      }
    }
  }

  function updateDiagram(root, bpIn, lenIn) {
    var s = 22;
    var e = 440;
    var range = e - s;
    var bpX = s + (bpIn / lenIn) * range;
    var bpLabel = root.querySelector('[data-diagram-bp-label]');
    var balanceThumb = root.querySelector('[data-diagram-balance-thumb]');
    var trackActive = root.querySelector('[data-diagram-track-active]');
    var txt = root.querySelector('[data-diagram-bp-txt]');
    var midTxt = root.querySelector('[data-diagram-mid-txt]');
    if (!txt || !midTxt) return;
    if (balanceThumb) {
      balanceThumb.setAttribute('cx', bpX);
    }
    if (trackActive) {
      var tw = Math.max(0, Math.min(range, bpX - s));
      trackActive.setAttribute('width', tw);
    }
    /** Pill group stays centered on the balance thumb (bpX); overflow visible on SVG so edges are not clipped). */
    if (bpLabel) {
      bpLabel.setAttribute('transform', 'translate(' + bpX + ', 24)');
    }
    syncDiagramPillGeometry(root);
    syncDiagramPillTextLayout(root);
    root.setAttribute('data-foc-diagram-len', String(lenIn));
    root.setAttribute('data-foc-diagram-bp', String(bpIn));
    syncDiagramViewBox(root);
  }

  function emitAnalytics(root, eventName, extra) {
    if (root.getAttribute('data-analytics') !== 'true') return;
    var sectionId = root.getAttribute('data-section-id') || '';
    var payload = Object.assign({ calculator_type: 'foc', section_id: sectionId }, extra || {});
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    }
    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', 'ArcheryCalculator', payload);
    }
  }

  function initRoot(root) {
    var lenRange = root.querySelector('[data-foc-len-range]');
    var lenNum = root.querySelector('[data-foc-len-number]');
    var bpRange = root.querySelector('[data-foc-bp-range]');
    var bpNum = root.querySelector('[data-foc-bp-number]');
    var btnIn = root.querySelector('[data-foc-unit-in]');
    var btnCm = root.querySelector('[data-foc-unit-cm]');
    var errEl = root.querySelector('[data-foc-error]');
    var resPct = root.querySelector('[data-foc-result-pct]');
    var resRating = root.querySelector('[data-foc-result-rating]');
    var ratingTile = root.querySelector('[data-foc-rating-tile]');
    var resBar = root.querySelector('[data-foc-result-bar]');
    var resDesc = root.querySelector('[data-foc-result-desc]');
    var liveAnnounce = root.querySelector('[data-foc-live-announce]');
    var hintLen = root.querySelector('[data-foc-hint-len]');
    var hintBp = root.querySelector('[data-foc-hint-bp]');

    var unit = 'in';
    var lenIn = 28.5;
    var bpIn = 16;
    var trackedView = false;
    var trackedUse = false;
    var liveAnnounceTimer = null;

    function clearRatingTile() {
      if (ratingTile) {
        ratingTile.classList.remove('is-rated');
        ratingTile.style.removeProperty('--calc-foc-rating-bg');
      }
    }

    function setRatingTile(r) {
      if (ratingTile && r && r.color) {
        ratingTile.style.setProperty('--calc-foc-rating-bg', r.color);
        ratingTile.classList.add('is-rated');
      }
    }

    function scheduleLiveAnnounce(text) {
      if (!liveAnnounce) return;
      clearTimeout(liveAnnounceTimer);
      liveAnnounceTimer = setTimeout(function () {
        liveAnnounce.textContent = text || '';
      }, 300);
    }

    function syncUIFromState(skipLenNum, skipBpNum) {
      var bpMax = getBpMaxIn(lenIn);
      bpIn = clamp(bpIn, BP_MIN, bpMax);

      if (hintLen) {
        hintLen.textContent =
          unit === 'in' ? 'Total length in inches' : 'Total length in centimeters';
      }
      if (hintBp) {
        hintBp.textContent =
          unit === 'cm' ? 'Distance from nock end (cm)' : 'Distance from nock end (inches)';
      }

      if (unit === 'in') {
        lenRange.min = String(LEN_MIN);
        lenRange.max = String(LEN_MAX);
        lenRange.step = String(STEP);
        bpRange.min = String(BP_MIN);
        bpRange.max = String(bpMax);
        bpRange.step = String(STEP);
      } else {
        lenRange.min = String(round2(LEN_MIN * CM_PER_IN));
        lenRange.max = String(round2(LEN_MAX * CM_PER_IN));
        lenRange.step = String(round2(STEP * CM_PER_IN));
        bpRange.min = String(round2(BP_MIN * CM_PER_IN));
        bpRange.max = String(round2(bpMax * CM_PER_IN));
        bpRange.step = String(round2(STEP * CM_PER_IN));
      }

      lenRange.value = String(
        unit === 'cm' ? round2(lenIn * CM_PER_IN) : round2(lenIn)
      );
      bpRange.value = String(
        unit === 'cm' ? round2(bpIn * CM_PER_IN) : round2(bpIn)
      );

      if (!skipLenNum) lenNum.value = formatMeasFromInches(lenIn, unit);
      if (!skipBpNum) bpNum.value = formatMeasFromInches(bpIn, unit);
    }

    function setUnit(u) {
      unit = u;
      if (btnIn && btnCm) {
        btnIn.setAttribute('aria-pressed', u === 'in' ? 'true' : 'false');
        btnCm.setAttribute('aria-pressed', u === 'cm' ? 'true' : 'false');
        btnIn.classList.toggle('is-active', u === 'in');
        btnCm.classList.toggle('is-active', u === 'cm');
      }
      syncUIFromState(false, false);
      calc();
    }

    function calc() {
      if (errEl) errEl.hidden = true;
      if (bpIn >= lenIn - 1e-9) {
        root.setAttribute('data-foc-diagram-len', String(lenIn));
        root.setAttribute('data-foc-diagram-bp', String(bpIn));
        syncDiagramViewBox(root);
        if (errEl) {
          errEl.textContent =
            'Balance point must be less than your total arrow length.';
          errEl.hidden = false;
        }
        if (resPct) resPct.innerHTML = '—';
        if (resRating) resRating.textContent = '—';
        clearRatingTile();
        if (resBar) {
          resBar.style.width = '0%';
          resBar.style.background = '';
        }
        if (resDesc) resDesc.textContent = '';
        scheduleLiveAnnounce('');
        return;
      }

      var foc = ((bpIn - lenIn / 2) / lenIn) * 100;
      var focR = Math.round(foc * 10) / 10;
      var r = getRating(foc);
      var barPct = Math.min(100, Math.max(1, (foc / 30) * 100));

      if (resPct) resPct.innerHTML = focR + '<span class="calc-foc__unit">%</span>';
      if (resRating) resRating.textContent = r.label;
      setRatingTile(r);
      if (resBar) {
        resBar.style.width = barPct.toFixed(1) + '%';
        resBar.style.background = r.color;
      }
      if (resDesc) resDesc.textContent = r.desc;

      scheduleLiveAnnounce(
        'Front of center ' +
          focR +
          ' percent. Rating: ' +
          r.label +
          '.'
      );

      updateDiagram(root, bpIn, lenIn);
    }

    function onLenRangeInput() {
      var raw = lenRange.value;
      var p = parseFloat(raw);
      if (isNaN(p)) return;
      lenIn =
        unit === 'cm'
          ? clamp(round2(p / CM_PER_IN), LEN_MIN, LEN_MAX)
          : clamp(round2(p), LEN_MIN, LEN_MAX);
      bpIn = clamp(bpIn, BP_MIN, getBpMaxIn(lenIn));
      syncUIFromState(false, document.activeElement === bpNum);
      calc();
      trackUse();
    }

    function onLenNumInput() {
      var committed = tryCommitLenInches(lenNum.value, unit);
      if (committed != null) {
        lenIn = committed;
        bpIn = clamp(bpIn, BP_MIN, getBpMaxIn(lenIn));
        syncUIFromState(true, document.activeElement === bpNum);
        calc();
        trackUse();
      }
    }

    function onLenNumBlur() {
      lenIn = blurCommitLen(lenNum.value, lenIn, unit);
      bpIn = clamp(bpIn, BP_MIN, getBpMaxIn(lenIn));
      syncUIFromState(false, document.activeElement === bpNum);
      calc();
    }

    function onBpRangeInput() {
      var raw = bpRange.value;
      var p = parseFloat(raw);
      if (isNaN(p)) return;
      var bpMax = getBpMaxIn(lenIn);
      bpIn =
        unit === 'cm'
          ? clamp(round2(p / CM_PER_IN), BP_MIN, bpMax)
          : clamp(round2(p), BP_MIN, bpMax);
      if (bpIn >= lenIn) bpIn = getBpMaxIn(lenIn);
      syncUIFromState(document.activeElement === lenNum, false);
      calc();
      trackUse();
    }

    function onBpNumInput() {
      var committed = tryCommitBpInches(bpNum.value, lenIn, unit);
      if (committed != null) {
        bpIn = committed;
        syncUIFromState(document.activeElement === lenNum, true);
        calc();
        trackUse();
      }
    }

    function onBpNumBlur() {
      bpIn = blurCommitBp(bpNum.value, lenIn, bpIn, unit);
      syncUIFromState(document.activeElement === lenNum, false);
      calc();
    }

    function trackUse() {
      if (!trackedUse) {
        trackedUse = true;
        emitAnalytics(root, 'archery_calculator_use', { engagement: 'adjust' });
      }
    }

    lenRange.addEventListener('input', onLenRangeInput);
    lenNum.addEventListener('input', onLenNumInput);
    lenNum.addEventListener('blur', onLenNumBlur);
    bpRange.addEventListener('input', onBpRangeInput);
    bpNum.addEventListener('input', onBpNumInput);
    bpNum.addEventListener('blur', onBpNumBlur);

    if (btnIn)
      btnIn.addEventListener('click', function () {
        setUnit('in');
      });
    if (btnCm)
      btnCm.addEventListener('click', function () {
        setUnit('cm');
      });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !trackedView) {
            trackedView = true;
            emitAnalytics(root, 'archery_calculator_view', { engagement: 'visible' });
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(root);

    ensureDiagramPillLayoutListeners();
    syncDiagramViewBox(root);
    syncDiagramPillGeometry(root);
    syncDiagramPillTextLayout(root);

    syncUIFromState(false, false);
    calc();
  }

  function initAll() {
    document.querySelectorAll('[data-foc-calculator]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
